import React, { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import QuickAddClientModal from "./QuickAddClientModal";
import SearchDropdown from "../applicationUi/components/SearchDropdown";
import { useDispatch, useSelector } from "react-redux";
import { fetchClinicianById } from "../store/thunks/cliniciansThunk";
import { createAppointment } from "../store/thunks/appointmentsThunk";
import {
  createIntakeForm,
  sendIntakeFormsEmail,
} from "../store/thunks/intakeThunk";
import { collectDepositPayment } from "../store/thunks/paymentsThunk";
import "react-datepicker/dist/react-datepicker.css";

const QuickAddIntake = ({
  isOpen,
  onClose,
  initialDate,
  initialTime,
  initialClinicianFname,
  initialClinicianLname,
  rescheduleData,
  onSave,
  selectedClinician: initialSelectedClinician,
  selectedClinic,
  mode = "create",
  clinic,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const paymentMethods = selectedClinic?.paymentMethods || [];

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [intakeFormId, setIntakeFormId] = useState(null);
  const [isNewClient, setIsNewClient] = useState(false);
  const [formsSent, setFormsSent] = useState(false);
  const [depositPaid, setDepositPaid] = useState(false);
  const [appointmentCreated, setAppointmentCreated] = useState(false);
  const [intakeFormUrl, setIntakeFormUrl] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [depositAmount, setDepositAmount] = useState("150.00");
  const [paymentType, setPaymentType] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [modalContent, setModalContent] = useState({
    date: initialDate || null,
    time: initialTime || "",
    clinicianFname: initialClinicianFname || "",
    clinicianLname: initialClinicianLname || "",
  });

  useEffect(() => {
    if (
      mode === "create" &&
      initialSelectedClinician &&
      initialSelectedClinician._id
    ) {
      dispatch(fetchClinicianById(initialSelectedClinician._id)).then(
        (response) => {
          setSelectedClinician(response.payload);
        }
      );
    }
  }, [initialSelectedClinician, mode, dispatch]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleNext = () => {
    if (currentStep === 1 && !selectedClient) {
      return; // Block next if no client selected in step 1
    }
    if (currentStep === 2 && (!selectedClinician || !selectedService)) {
      return; // Block next if clinician and service not selected in step 2
    }
    setCurrentStep((prev) => Math.min(prev + 1, 4));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setModalContent((prevContent) => ({
      ...prevContent,
      [name]: value,
    }));
  };

  const handleDateChange = (e) => {
    const newDateStr = e.target.value;
    if (newDateStr) {
      const [year, month, day] = newDateStr.split("-");
      const updatedDate = new Date(modalContent.date || Date.now());
      updatedDate.setFullYear(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day)
      );
      setModalContent((prev) => ({ ...prev, date: updatedDate }));
    }
  };

  const handleServiceSelection = (service) => {
    setSelectedService(service);
  };

  const handleCreateAppointment = async () => {
    const { date, time } = modalContent;

    if (!time) {
      console.error("Time is not defined");
      return;
    }

    console.log("[QuickAddIntake] time", time);

    if (
      !selectedClient ||
      !selectedService ||
      !selectedClinician ||
      !selectedClinician.defaultLocation
    ) {
      alert("All fields must be nonempty");
      return;
    }

    const [hours, minutes] = time.split(":").map(Number);
    const start = new Date(date);
    start.setHours(hours, minutes, 0, 0);
    const end = new Date(start);
    end.setMinutes(start.getMinutes() + (selectedService.duration || 30));

    const appointmentData = {
      clinic: selectedClinician.defaultLocation,
      clinician: selectedClinician._id,
      client: selectedClient._id,
      title: selectedService.description,
      service: selectedService._id,
      start: start.toISOString(),
      end: end.toISOString(),
      userId: user._id,
    };

    setIsLoading(true);
    try {
      await dispatch(createAppointment(appointmentData)).unwrap();
      setAppointmentCreated(true);
      handleNext();
    } catch (error) {
      console.error("Failed to create appointment:", error);
      alert("Failed to create appointment. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateIntakeForms = async () => {
    const formData = {
      client: {
        firstName: selectedClient.firstName,
        lastName: selectedClient.lastName,
        dob: selectedClient.dateOfBirth,
        address: {
          aptUnit: selectedClient.address?.aptUnit || "",
          streetNumber: selectedClient.address?.streetNumber || "",
          streetName: selectedClient.address?.streetName || "",
          city: selectedClient.address?.city || "",
          province: selectedClient.address?.province || "",
          postalCode: selectedClient.address?.postalCode || "",
          country: selectedClient.address?.country || "Canada",
        },
      },
      createdClient: selectedClient._id,
      clinician: selectedClinician._id,
      service: selectedService._id,
      createdBy: user._id,
    };

    setIsLoading(true);
    try {
      const response = await dispatch(createIntakeForm(formData)).unwrap();
      setIntakeFormId(response._id);
      setIntakeFormUrl(`${window.location.origin}/intake/${response._id}`);
    } catch (error) {
      console.error("Failed to create intake form:", error);
      alert("Failed to create intake form. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendIntakeEmail = async () => {
    const data = {
      clientId: selectedClient._id,
      clientEmail: selectedClient.email,
      fromEmail: selectedClinic.email,
      fromEmailName: selectedClinic.name,
      clientName: selectedClient.firstName + " " + selectedClient.lastName,
      intakeUrl: intakeFormUrl,
      selectedService: selectedService,
      selectedClinician: selectedClinician,
      appointmentDate: modalContent.date,
      appointmentTime: modalContent.time,
    };
    setIsLoading(true);
    try {
      await dispatch(
        sendIntakeFormsEmail({
          id: intakeFormId,
          data: data,
        })
      ).unwrap();
      setEmailSent(true);
    } catch (error) {
      console.error("Failed to send intake email:", error);
      alert("Failed to send intake email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCollectDeposit = async () => {
    const paymentData = {
      paymentMethod: paymentType,
      paymentAmount: parseFloat(depositAmount),
      paymentDate: new Date().toISOString(),
      createdBy: user._id,
      clientId: selectedClient._id,
    };

    setIsLoading(true);
    try {
      await dispatch(collectDepositPayment(paymentData)).unwrap();
      setDepositPaid(true);
    } catch (error) {
      console.error("Failed to process deposit:", error);
      alert("Failed to process deposit. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="text-sm font-medium text-muted-foreground mb-2">
              Client Selection
            </div>

            {selectedClient && (
              <div className="p-4 border rounded-sm">
                <p className="text-sm text-gray-600">
                  Selected: {selectedClient.firstName} {selectedClient.lastName}
                </p>
              </div>
            )}

            <SearchDropdown
              prefix=":client:"
              displayFields={["firstName", "lastName", "email"]}
              onSelection={setSelectedClient}
              placeholder="Search for existing client"
            />

            <button
              onClick={() => setIsNewClient(true)}
              className="w-full mt-2 px-4 py-2 bg-indigo-600 text-white rounded-sm hover:bg-indigo-500">
              Create New Client
            </button>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Confirm Appointment for {selectedClient?.firstName}{" "}
              {selectedClient?.lastName}
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={
                    modalContent.date
                      ? new Date(modalContent.date).toISOString().split("T")[0]
                      : ""
                  }
                  onChange={handleDateChange}
                  className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Time
                </label>
                <input
                  type="time"
                  name="time"
                  value={modalContent.time}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm"
                />
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">
                Treating clinician:
              </div>
              {selectedClinician && (
                <div className="mt-2 p-2 border rounded-sm mb-2">
                  <p className="text-sm text-gray-800">
                    Selected Clinician: {selectedClinician.firstName}{" "}
                    {selectedClinician.lastName}
                  </p>
                </div>
              )}
              <SearchDropdown
                prefix=":user:"
                displayFields={["firstName", "lastName"]}
                onSelection={setSelectedClinician}
                placeholder="Select clinician"
              />
            </div>

            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">
                Service:
              </div>
              {selectedService && (
                <div className="mt-2 p-2 border rounded-sm mb-2">
                  <p className="text-sm text-gray-800">
                    Selected Service: {selectedService.description} - $
                    {selectedService.price}
                  </p>
                </div>
              )}
              <SearchDropdown
                prefix=":service:"
                displayFields={["description", "price"]}
                onSelection={handleServiceSelection}
                placeholder="Select service"
              />
            </div>

            <button
              onClick={handleCreateAppointment}
              disabled={!selectedClinician || !selectedService || isLoading}
              className={`w-full px-4 py-2 rounded-sm ${
                !selectedClinician || !selectedService || isLoading
                  ? "bg-gray-200 cursor-not-allowed"
                  : "bg-indigo-600 text-white hover:bg-indigo-500"
              }`}>
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </span>
              ) : (
                "Create Appointment"
              )}
            </button>

            {appointmentCreated && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-sm">
                <p className="text-green-600 text-sm font-medium">
                  Appointment created successfully!
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Take Deposit
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Deposit Amount
                </label>
                <div className="mt-1 relative rounded-sm shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="text"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-sm"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Payment Type
                </label>
                <select
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  className="block w-full mt-2 rounded-sm border-gray-300 shadow-sm">
                  <option value="">Select Method</option>
                  {paymentMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.englishDescription}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleCollectDeposit}
                disabled={!paymentType || depositPaid || isLoading}
                className={`w-full px-4 py-2 rounded-sm ${
                  !paymentType || depositPaid || isLoading
                    ? "bg-gray-200 cursor-not-allowed"
                    : "bg-indigo-600 text-white hover:bg-indigo-500"
                }`}>
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  "Collect Deposit"
                )}
              </button>

              {depositPaid && (
                <p className="text-sm text-green-600">
                  ✓ Deposit of ${depositAmount} has been processed successfully
                  via {paymentType}
                </p>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Send Intake Email
            </h3>

            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                Send intake email to client: {selectedClient?.email}
              </p>

              {!intakeFormUrl ? (
                <button
                  onClick={handleCreateIntakeForms}
                  disabled={isLoading}
                  className={`w-full px-4 py-2 ${
                    isLoading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-500"
                  } text-white rounded-sm`}>
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </span>
                  ) : (
                    "Create Intake Form"
                  )}
                </button>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-sm">
                    <p className="text-sm font-medium text-gray-900">
                      Intake Form URL:
                    </p>
                    <a
                      href={intakeFormUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 break-all">
                      {intakeFormUrl}
                    </a>
                  </div>

                  <button
                    onClick={handleSendIntakeEmail}
                    disabled={isLoading || emailSent}
                    className={`w-full px-4 py-2 ${
                      isLoading || emailSent
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-indigo-600 hover:bg-indigo-500"
                    } text-white rounded-sm`}>
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : emailSent ? (
                      "Email Sent ✓"
                    ) : (
                      "Send Intake Email"
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-60 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-900 opacity-50" onClick={onClose} />
      <div className="bg-white rounded-sm p-8 max-w-4xl w-full mx-4 relative z-60 overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">
            Create New Intake - Step {currentStep} of 4
          </h2>
          <button onClick={onClose}>
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`w-1/4 h-2 rounded-full mx-1 ${
                  step <= currentStep ? "bg-indigo-600" : "bg-gray-200"
                }`}
              />
            ))}
          </div>
        </div>

        {renderStepContent()}

        <div className="mt-6 flex justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 1 || isLoading}
            className={`px-4 py-2 rounded-sm ${
              currentStep === 1 || isLoading
                ? "bg-gray-200 cursor-not-allowed"
                : "bg-gray-600 text-white hover:bg-gray-500"
            }`}>
            Back
          </button>

          <button
            onClick={
              currentStep === 4
                ? () => {
                    if (onSave) {
                      onSave({
                        client: selectedClient,
                        service: selectedService,
                        clinician: selectedClinician,
                        date: modalContent.date,
                        time: modalContent.time,
                        clinic: selectedClinic,
                      });
                    }
                    onClose();
                  }
                : handleNext
            }
            disabled={
              (currentStep === 1 && !selectedClient) ||
              (currentStep === 2 && !appointmentCreated) ||
              isLoading
            }
            className={`px-4 py-2 rounded-sm ${
              (currentStep === 1 && !selectedClient) ||
              (currentStep === 2 && !appointmentCreated) ||
              isLoading
                ? "bg-gray-200 cursor-not-allowed"
                : "bg-indigo-600 text-white hover:bg-indigo-500"
            }`}>
            {currentStep === 4
              ? "Complete"
              : currentStep === 3 && !depositPaid
              ? "Skip Payment"
              : "Next"}
          </button>
        </div>

        {isNewClient && (
          <QuickAddClientModal onClose={() => setIsNewClient(false)} />
        )}
      </div>
    </div>
  );
};

export default QuickAddIntake;
